import { pick } from 'ramda'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { listTablePrefs } from 'app/constants'
import DocumentMeta from 'core/components/DocumentMeta'
import ExternalLink from 'core/components/ExternalLink'
import { DateAndTime } from 'core/components/listTable/cells/DateCell'
import PollingData from 'core/components/PollingData'
import SimpleLink from 'core/components/SimpleLink'
import TitleWithInfoIcon from 'core/components/TitleWithInfoIcon'
import ListContainer from 'core/containers/ListContainer'
import Badge from 'core/elements/badge'
import type { GridViewColumn } from 'core/elements/grid/Grid'
import Text from 'core/elements/Text'
import Tooltip from 'core/elements/tooltip'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { routeManager } from 'core/utils/routes'
import ConfigureMetalLBModal from 'k8s/components/clusters-dashboard/ConfigureMetalLBModal'
import { IClusterDetailTabs } from 'k8s/components/dashboard/ClusterDetailTabs'
import { listKaapiClusterAddons } from 'k8s/components/kaapi/cluster-addons/actions'
import { kaapiConfigMapsSelector } from 'k8s/components/kaapi/config-maps/selectors'
import DataKeys from 'k8s/DataKeys'
import { extractSupportedK8sVersions } from '../cluster/deployment/form-components/helpers'
import { getBadgeVariant, getClusterEndpoint, getClusterStatus } from '../dashboard/helpers'
import { ClusterStatus } from '../kaapi/cluster-upgrade/model'
import { listKaapiClusters } from '../kaapi/clusters/actions'
import { getClusterHealth } from '../kaapi/clusters/clusterHealth'
import { kaapiClustersOverviewSelector } from '../kaapi/clusters/selectors'
import { listKaapiConfigMaps } from '../kaapi/config-maps/actions'
import MachineErrorModal from '../kaapi/machines/MachineErrorModal'
import {
  isUnhealthy,
  MachineHealthState,
  machineHealthSeverityRank,
} from '../kaapi/machines/machineHealth'
import { listByoHosts } from '../nodes/byoh/actions'
import DeleteK8sClustersDialog from './DeleteK8sClustersDialog'
import K8sVersionCellComponent from './K8sVersionCellComponent'

const usePrefParams = createUsePrefParamsHook('Clusters', listTablePrefs)

export const KaapiClusterStatusBadge = ({ cluster }) => {
  const [showErrorModal, setShowErrorModal] = useState(false)
  const clusterStatus = useMemo(() => getClusterStatus(cluster), [cluster])
  const clusterHealth = useMemo(
    () => getClusterHealth(cluster, undefined, cluster?.infraFailureEvent),
    [cluster],
  )

  const worstMachine = useMemo(() => {
    const unhealthy = (cluster?.machines || [])
      .filter((m) => isUnhealthy(m?.machineHealth))
      .sort(
        (a, b) =>
          (machineHealthSeverityRank[b?.machineHealth?.state] || 0) -
          (machineHealthSeverityRank[a?.machineHealth?.state] || 0),
      )
    return unhealthy[0]
  }, [cluster])

  if (isUnhealthy(clusterHealth)) {
    return (
      <>
        <Badge
          data-testid="overview-tab-emp-status"
          variant="error"
          text={`${clusterStatus} - Error`}
          ellipsisAt={20}
          tooltipBody="Click to view error details"
          onClick={(e) => {
            e.stopPropagation()
            setShowErrorModal(true)
          }}
        />
        {showErrorModal && (
          <MachineErrorModal
            machineName={cluster?.metadata?.name}
            reason={clusterHealth?.reason}
            message={clusterHealth?.message}
            onClose={() => setShowErrorModal(false)}
          />
        )}
      </>
    )
  }

  const isScalingWithIssues =
    ['Scaling', 'ScalingUp', 'ScalingDown'].includes(clusterStatus) && !!worstMachine

  // A Degraded/Failed machine is a real error that blocks the VM and will not
  // self-resolve (e.g. quota 413), so escalate to a red Error. The only other
  // state isUnhealthy() admits is Stuck (join timeout), which is ambiguous and
  // may still resolve, so it stays yellow "Not Joining" - same label the
  // machine-level badge uses for Stuck, since that's exactly what this is.
  const worstIsError =
    worstMachine?.machineHealth?.state === MachineHealthState.Degraded ||
    worstMachine?.machineHealth?.state === MachineHealthState.Failed

  const stuckLabel =
    worstMachine?.machineHealth?.reason === 'InfrastructureProvisionTimeout'
      ? 'Provisioning Stuck'
      : 'Not Joining'

  if (isScalingWithIssues) {
    return (
      <>
        <Badge
          data-testid="overview-tab-emp-status"
          variant={worstIsError ? 'error' : 'warning'}
          text={`${clusterStatus} - ${worstIsError ? 'Error' : stuckLabel}`}
          ellipsisAt={30}
          tooltipBody="Click to view error details"
          onClick={(e) => {
            e.stopPropagation()
            setShowErrorModal(true)
          }}
        />
        {showErrorModal && (
          <MachineErrorModal
            machineName={worstMachine?.name}
            reason={worstMachine?.machineHealth?.reason}
            message={worstMachine?.machineHealth?.message}
            instanceId={
              worstMachine?.instanceId ||
              worstMachine?.openStackServer?.status?.instanceID ||
              (worstMachine?.spec?.providerID?.startsWith('openstack:///')
                ? worstMachine?.spec?.providerID?.replace('openstack:///', '')
                : undefined)
            }
            onClose={() => setShowErrorModal(false)}
          />
        )}
      </>
    )
  }

  return (
    <Tooltip
      message={
        clusterStatus === 'Unhealthy' &&
        cluster?.status?.failureReason &&
        cluster?.status?.failureMessage
          ? `${cluster?.status?.failureReason}: ${cluster?.status?.failureMessage}`
          : null
      }
    >
      <Badge
        data-testid="overview-tab-emp-status"
        variant={getBadgeVariant(clusterStatus)}
        text={clusterStatus}
        ellipsisAt={20}
        tooltipBody={''}
      />
    </Tooltip>
  )
}

// Stable module-level cell so the grid does not rebuild it every render, and
// paired with memoizeCell:false below. KaapiClusterStatusBadge is stateful
// (showErrorModal); behind the grid's default memoizeShallow cache the badge's
// setState never re-renders (moize serves the element cached by props), so the
// Issues/Error badge click would do nothing. Same idiom as MachineStatusCell.
const ClusterStatusCell = ({ item }) => <KaapiClusterStatusBadge cluster={item} />

const defaultParams = {
  orderBy: 'metadata.creationTimestamp',
  orderDirection: 'desc',
}

const searchTargets = ['clusterName', 'clusterPhase']

const searchFunctions = [
  {
    property: 'clusterPhase',
    searchFn: (value, searchString, item) => {
      const status = getClusterStatus(item)
      return status.toLowerCase().includes(searchString.toLowerCase())
    },
  },
]

export default function ListK8sClusters() {
  const { params, getParamsUpdater } = usePrefParams(defaultParams)
  const { history } = useReactRouter()
  const { updatePrefs } = useScopedPreferences('k8sPluginGlobalParams')

  const [showConfigureMetalLBModal, setShowConfigureMetalLBModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedClusters, setSelectedClusters] = useState([])
  const [selectedDeleteRows, setSelectedDeleteRows] = useState([])

  // Load the list of clusters
  const { loading: loadingKaapiClusters, reload: reloadKaapiClusters } =
    useListAction(listKaapiClusters)
  const { loading: loadingKaapiClusterAddons, reload: reloadClusterAddons } =
    useListAction(listKaapiClusterAddons)
  const clusters = useSelector(kaapiClustersOverviewSelector)

  // Load the list of config maps
  const { loading: loadingConfigMaps } = useListAction(listKaapiConfigMaps)
  // BYOH install/join failures are reported only on the ByoHost, which the
  // machine health classifier reads to tell a failed join from an unclaimed host.
  // Gated on at least one BYOH cluster being present so an OpenStack-only
  // deployment never issues the byohosts/byomachines requests - the CRDs need
  // not even be installed there.
  useListAction(listByoHosts, {
    preRequisites: !!clusters?.some((cluster) => cluster?.isBYOHCluster),
  })
  const configMaps = useSelector(kaapiConfigMapsSelector)
  // Get the supported K8s versions from the config maps
  const supportedK8sVersions = useMemo(
    () => extractSupportedK8sVersions(configMaps).sort(),
    [configMaps],
  )

  const onReload = useCallback(async () => {
    await Promise.all([reloadKaapiClusters(true, false), reloadClusterAddons(true, false)])
  }, [reloadKaapiClusters, reloadClusterAddons])

  // Reload the list of clusters on first load
  useEffect(() => {
    onReload()
  }, [])

  const columns: GridViewColumn<any>[] = [
    {
      key: 'clusterName',
      label: 'Name',
      CellComponent: (props) => {
        const clusterPath = routeManager.routes.kubernetes.infrastructure.cluster.path({
          cluster: props?.item?.metadata?.name,
          tab: IClusterDetailTabs.OVERVIEW,
        })
        return (
          <SimpleLink
            src={clusterPath}
            onClick={() => {
              updatePrefs({
                cluster: props?.item?.metadata?.name,
                cluster_endpoint: getClusterEndpoint(props?.item?.spec?.controlPlaneEndpoint?.host),
                clusterId: props?.item?.metadata?.uid,
                hcp_endpoint:
                  props?.item?.hostedControlPlane?.status?.apiServerUrl ||
                  props?.item?.metadata?.annotations?.[
                    'colocatedcontrolplane.platform9.io/apiserver-url'
                  ],
              })
            }}
          >
            {props?.item?.metadata?.name}
          </SimpleLink>
        )
      },
    },
    {
      key: 'clusterPhase',
      label: 'Status',
      CellComponent: ClusterStatusCell,
      memoizeCell: false,
    },
    {
      key: 'infrastructureProvider',
      label: 'Infrastructure Provider',
    },
    {
      key: 'k8Version',
      label: 'K8s Version',
      CellComponent: ({ item }) => (
        <K8sVersionCellComponent item={item} supportedK8sVersions={supportedK8sVersions} />
      ),
    },

    // // TODO:: Add support period
    // {
    //   key: 'status.replicas',
    //   label: 'Support Period',
    //   formatFn: cellFormatFn,
    // },
    // // TODO:: need to confirm this and update the logic for the same currently added status logic
    // {
    //   key: 'status.readyReplicas',
    //   label: 'Nodes Status',
    //   //  TODO:: need to confirm this and update the logic for the same
    //   CellComponent: (props) => {
    //     const isClusterHealthy = isKaapiClusterHealthy(props?.item)

    //     return (
    //       <Badge
    //         data-testid={'overview-tab-emp-status'}
    //         variant={isClusterHealthy && !isClusterDeleting(props?.item) ? 'success' : 'error'}
    //         text={
    //           isClusterDeleting(props?.item)
    //             ? 'Deleting'
    //             : isClusterHealthy
    //             ? 'Healthy'
    //             : 'Unhealthy'
    //         }
    //         ellipsisAt={20}
    //         tooltipBody={''}
    //       />
    //     )
    //   },
    // },
    {
      key: 'controlPlaneType',
      label: 'Control Plane Type',
    },
    {
      key: 'isHighlyAvailable',
      label: (
        <TitleWithInfoIcon
          titleText="High Availability"
          tooltipMsg="Clusters with 3 or more control plane nodes and 2 or more worker nodes"
        />
      ),
      CellComponent: (props) => {
        if (!props?.value) return <Text variant="body2">-</Text>
        return (
          <Badge
            text={props.value}
            variant={props.value === 'Yes' ? 'success' : 'warning'}
            hideTooltip
          />
        )
      },
    },
    {
      key: 'spec.controlPlaneEndpoint.host',
      label: 'API Endpoint',
      CellComponent: ({ item }) => (
        <Text variant="body2">{getClusterEndpoint(item?.spec?.controlPlaneEndpoint?.host)}</Text>
      ),
    },
    {
      key: 'metadata.creationTimestamp',
      label: 'Created',
      CellComponent: DateAndTime,
    },
  ]
  const batchActions = useMemo(
    () => [
      {
        label: 'Delete',
        icon: 'trash-alt',
        cond: (selectedItems) =>
          selectedItems?.every((item) => item?.clusterPhase !== ClusterStatus.Deleting),
        disabledTooltip: (selectedItems) =>
          selectedItems?.some((item) => item?.clusterPhase === ClusterStatus.Deleting)
            ? 'Selected clusters are already being deleted'
            : '',
        handleAction: (selectedItems) => {
          setSelectedDeleteRows(selectedItems)
          setShowDeleteModal(true)
        },
      },
      {
        label: 'Expose LB',
        icon: 'gear',
        refreshAfterSuccess: true,
        handleAction: (cluster) => {
          setSelectedClusters(cluster)
          setShowConfigureMetalLBModal(true)
        },
        cond: (clusters) => {
          if (clusters?.length !== 1) return false
          if (clusters?.some((cluster) => cluster.isBYOHCluster)) return false
          return true
        },
        disabledTooltip: (clusters) => {
          if (clusters?.length !== 1) return 'Select a single cluster to configure MetalLB'
          if (clusters?.some((cluster) => cluster.isBYOHCluster))
            return 'MetalLB can be configured only for managed control planes'
          return ''
        },
      },
    ],
    [setShowConfigureMetalLBModal, setSelectedClusters],
  )

  return (
    <>
      <PollingData
        hidden
        loading={loadingKaapiClusters}
        onReload={onReload}
        refreshDuration={1000 * 10}
        pause={showConfigureMetalLBModal}
      />
      <DocumentMeta title="Clusters" />
      {showConfigureMetalLBModal && (
        <ConfigureMetalLBModal
          onClose={() => setShowConfigureMetalLBModal(false)}
          rows={selectedClusters}
          reload={onReload}
        />
      )}
      {showDeleteModal && (
        <DeleteK8sClustersDialog
          rows={selectedDeleteRows}
          onClose={() => setShowDeleteModal(false)}
        />
      )}
      <ListContainer<any, any>
        dataKey={DataKeys.KaapiClusters}
        searchTargets={searchTargets}
        searchFunctions={searchFunctions}
        uniqueIdentifier="uid"
        loading={loadingKaapiClusters || loadingConfigMaps || loadingKaapiClusterAddons}
        loadingMessage="Loading Clusters..."
        onRefresh={reloadKaapiClusters}
        multiSelection
        data={clusters}
        columns={columns}
        getParamsUpdater={getParamsUpdater}
        label="Clusters"
        showItemsCountInLabel
        showBreadcrumbs={false}
        batchActions={batchActions}
        emptyContent="No clusters found"
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}
